import { PartialStoryFn, StoryContext } from '@storybook/addons';
import { html } from 'lit-html';
import { ifDefined } from 'lit-html/directives/if-defined';
import { Input } from '../input';

const meta = {
  args: {
    disabled: false,
    error: false,
    errorMessage: '',
    helperText: '',
    hideIconEnd: false,
    hideIconStart: false,
    iconEnd: '',
    iconStart: '',
    max: '',
    maxlength: '',
    min: '',
    minlengthMessage: '',
    minlength: '',
    name: 'input-name',
    pattern: '',
    patternMessage: '',
    placeholder: '',
    readonly: false,
    required: false,
    requiredMessage: '',
    showLabel: true,
    tooltip: '',
    value: '',
    width: '',
  },
  argTypes: {
    disabled: { table: { disable: true } },
    error: { table: { disable: true } },
    errorMessage: { table: { disable: true } },
    helperText: { table: { disable: true } },
    hideIconEnd: { table: { disable: true } },
    hideIconStart: { table: { disable: true } },
    iconEnd: { table: { disable: true } },
    iconStart: { table: { disable: true } },
    interactiveButtonLabel: { table: { disable: true } },
    max: { table: { disable: true } },
    maxlength: { table: { disable: true } },
    min: { table: { disable: true } },
    minlengthMessage: { table: { disable: true } },
    minlength: { table: { disable: true } },
    name: { table: { disable: true } },
    pattern: { table: { disable: true } },
    patternMessage: { table: { disable: true } },
    placeholder: { table: { disable: true } },
    readonly: { table: { disable: true } },
    required: { table: { disable: true } },
    requiredMessage: { table: { disable: true } },
    showLabel: { table: { disable: true } },
    tooltip: { table: { disable: true } },
    type: { table: { disable: true } },
    value: { table: { disable: true } },
    width: { table: { disable: true } },
  },
  decorators: [
    (story: PartialStoryFn, { args: { tooltip } }: StoryContext) =>
      html`
        <style type="text/css">
          abds-input {
            padding-top: ${tooltip ? '2rem' : ''};
          }
        </style>
        ${story()}
      `,
  ],
  parameters: {
    controls: {
      hideNoControlsWarning: true,
      sort: 'requiredFirst',
    },
  },
  title: 'Components/Input/',
};

interface Story {
  disabled: Input['disabled'];
  error: Input['error'];
  errorMessage: Input['errorMessage'];
  helperText: Input['helperText'];
  hideIconEnd: Input['hideIconEnd'];
  hideIconStart: Input['hideIconStart'];
  iconEnd: Input['iconEnd'];
  iconStart: Input['iconStart'];
  id: string;
  interactiveButtonLabel: string;
  max: Input['max'];
  maxlength: Input['maxlength'];
  min: Input['min'];
  minlengthMessage: Input['minlengthMessage'];
  minlength: Input['minlength'];
  name: Input['name'];
  pattern: Input['pattern'];
  patternMessage: Input['patternMessage'];
  placeholder: Input['placeholder'];
  readonly: Input['readonly'];
  required: Input['required'];
  requiredMessage: Input['requiredMessage'];
  showLabel: boolean;
  tooltip: Input['tooltip'];
  type: Input['type'];
  value: Input['value'];
  width: Input['width'];
}

const createInputLabel = (showLabel: boolean, type = 'text') =>
  showLabel ? `${type.charAt(0).toUpperCase()}${type.slice(1)} input label` : '';

const story = ({
  disabled,
  error,
  errorMessage,
  helperText,
  hideIconEnd,
  hideIconStart,
  iconEnd,
  iconStart,
  id,
  interactiveButtonLabel,
  max,
  maxlength,
  min,
  minlengthMessage,
  minlength,
  name,
  pattern,
  patternMessage,
  placeholder,
  readonly,
  required,
  requiredMessage,
  showLabel,
  tooltip,
  type,
  value,
  width,
}: Story) => html`
  <abds-input
    disabled="${ifDefined(disabled || undefined)}"
    error="${ifDefined(error || undefined)}"
    error-message="${ifDefined(errorMessage || undefined)}"
    helper-text="${ifDefined(helperText || undefined)}"
    hide-icon-end="${ifDefined(hideIconEnd || undefined)}"
    hide-icon-start="${ifDefined(hideIconStart || undefined)}"
    icon-end="${ifDefined(iconEnd || undefined)}"
    icon-start="${ifDefined(iconStart || undefined)}"
    id="${ifDefined(id || undefined)}"
    interactive-button-label="${ifDefined(interactiveButtonLabel || undefined)}"
    max="${ifDefined(max || undefined)}"
    maxlength="${ifDefined(maxlength || undefined)}"
    min="${ifDefined(min || undefined)}"
    minlength-message="${ifDefined(minlengthMessage || undefined)}"
    minlength="${ifDefined(minlength || undefined)}"
    name="${ifDefined(name || undefined)}"
    pattern-message="${ifDefined(patternMessage || undefined)}"
    pattern="${ifDefined(pattern || undefined)}"
    placeholder="${ifDefined(placeholder || undefined)}"
    readonly="${ifDefined(readonly || undefined)}"
    required-message="${ifDefined(requiredMessage || undefined)}"
    required="${ifDefined(required || undefined)}"
    tooltip="${ifDefined(tooltip || undefined)}"
    type="${ifDefined(type || undefined)}"
    value="${ifDefined(value || undefined)}"
    width="${ifDefined(width || undefined)}"
  >
    ${createInputLabel(showLabel, type)}
  </abds-input>
`;

export { meta, story };
